import {
  freePtacWallSlotsWithin,
  nearestPtacSlot,
  pointAlongPtacWall,
  precisePoint,
  PTAC_UNIT_GRID_SQUARES,
  ptacExteriorWallLength,
  ptacExteriorWalls,
  ptacWallAlongDistance,
  ptacWallAlongSpan
} from '@alpinehomeair/common';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  FootprintPolygonsType,
  GridSpaceDataType,
  PolygonEdge,
  PositionType,
  PtacExteriorWall,
  PtacWallSpan
} from 'data/types';
import {
  PtacLevelContext,
  PtacRoomBounds,
  PtacRoomExteriorEdge,
  PtacUnitPlacement,
  PtacWallAlong,
  PtacWallCandidate
} from 'utils/ptac-unit-placement-types';
import {
  PtacWallOpening,
  PtacWallOpenings,
  PtacWallOpeningsLookup
} from 'utils/ptac-wall-openings-types';
import {
  EdgeCardinal,
  isPolygonGridSpace,
  rectToVertices
} from 'utils/space-polygon';
import { rotationDegFromOutwardNormal } from 'utils/wall-segment';

const HALF_UNIT = PTAC_UNIT_GRID_SQUARES / 2;
const OPENING_ON_WALL_TOLERANCE_FT = 0.1;

const EDGE_PRIORITY: readonly EdgeCardinal[] = [
  'top',
  'right',
  'bottom',
  'left'
];

const NO_OPENINGS: PtacWallOpenings = [];

export const noPtacWallOpenings: PtacWallOpeningsLookup = () => NO_OPENINGS;

function roomBoundsInGrid(room: GridSpaceDataType): PtacRoomBounds {
  return {
    left: Math.round(room.position.x / STRUCTURE_GRID_SIZE),
    right: Math.round(
      (room.position.x + room.position.width) / STRUCTURE_GRID_SIZE
    ),
    top: Math.round(room.position.y / STRUCTURE_GRID_SIZE),
    bottom: Math.round(
      (room.position.y + room.position.height) / STRUCTURE_GRID_SIZE
    )
  };
}

function roomPolygonInGrid(room: GridSpaceDataType): PositionType[] {
  const { x, y, width, height } = room.position;
  const vertices = isPolygonGridSpace(room.vertices, width, height)
    ? room.vertices ?? []
    : rectToVertices(width, height);
  return vertices.map((vertex) => ({
    x: (x + vertex.x) / STRUCTURE_GRID_SIZE,
    y: (y + vertex.y) / STRUCTURE_GRID_SIZE
  }));
}

function clampAlongWall(along: number, wall: PtacWallSpan): number {
  const max = wall.end - PTAC_UNIT_GRID_SQUARES;
  if (max < wall.start) {
    return Math.round((wall.start + wall.end) / 2 - HALF_UNIT);
  }
  return Math.min(Math.max(Math.round(along), wall.start), max);
}

const wallMidpoint = (wall: PtacWallSpan): number =>
  (wall.start + wall.end) / 2 - HALF_UNIT;

const fullWallSpan = (wall: PolygonEdge): PtacWallSpan => ({
  start: 0,
  end: wall.length
});

function distanceToWallLine(wall: PolygonEdge, point: PositionType): number {
  return Math.abs(
    (point.x - wall.p1.x) * wall.outward.x +
      (point.y - wall.p1.y) * wall.outward.y
  );
}

function openingLiesOnWall(
  wall: PolygonEdge,
  opening: PtacWallOpening
): boolean {
  return (
    distanceToWallLine(wall, opening.start) <= OPENING_ON_WALL_TOLERANCE_FT &&
    distanceToWallLine(wall, opening.end) <= OPENING_ON_WALL_TOLERANCE_FT
  );
}

function blockedSpansOnWall(
  wall: PolygonEdge,
  openings: PtacWallOpenings
): PtacWallSpan[] {
  return openings
    .filter((opening) => openingLiesOnWall(wall, opening))
    .map((opening) => ptacWallAlongSpan(wall, opening.start, opening.end));
}

function wallCandidate(
  wall: PtacExteriorWall,
  openings: PtacWallOpenings
): PtacWallCandidate {
  return {
    edge: wall.edge.cardinal,
    wall: wall.edge,
    slots: freePtacWallSlotsWithin(
      wall.exteriorSpans,
      blockedSpansOnWall(wall.edge, openings)
    ),
    lengthPx: ptacExteriorWallLength(wall) * STRUCTURE_GRID_SIZE,
    rotationDeg: rotationDegFromOutwardNormal(wall.edge.outward)
  };
}

const byEdgePriority = (a: PtacWallCandidate, b: PtacWallCandidate): number =>
  EDGE_PRIORITY.indexOf(a.edge) - EDGE_PRIORITY.indexOf(b.edge) ||
  a.wall.index - b.wall.index;

function exteriorWallCandidates(
  room: GridSpaceDataType,
  level: PtacLevelContext
): PtacWallCandidate[] {
  const openings = level.openings(room);
  return ptacExteriorWalls({
    polygon: roomPolygonInGrid(room),
    siblings: level.spaces
      .filter((space) => space.id !== room.id)
      .map(roomPolygonInGrid),
    footprints: level.footprints
  })
    .map((wall) => wallCandidate(wall, openings))
    .sort(byEdgePriority);
}

function alongWallClearOfOpenings(
  candidate: PtacWallCandidate,
  along: number
): PtacWallAlong {
  const free = nearestPtacSlot(along, candidate.slots);
  return free === null
    ? {
        along: clampAlongWall(along, fullWallSpan(candidate.wall)),
        fitsUnit: false
      }
    : { along: free, fitsUnit: true };
}

function positionOnWall(wall: PolygonEdge, alongWall: number): PositionType {
  const center = pointAlongPtacWall(wall, alongWall + HALF_UNIT);
  return precisePoint({ x: center.x - HALF_UNIT, y: center.y - HALF_UNIT });
}

function targetAlongWall(wall: PolygonEdge, target: PositionType): number {
  const center = { x: target.x + HALF_UNIT, y: target.y + HALF_UNIT };
  return ptacWallAlongDistance(wall, center) - HALF_UNIT;
}

function candidatesThatFitUnit(
  candidates: PtacWallCandidate[]
): PtacWallCandidate[] {
  const fitting = candidates.filter((candidate) => candidate.slots.length > 0);
  return fitting.length > 0 ? fitting : candidates;
}

function candidatePlacement(
  candidate: PtacWallCandidate,
  along: number
): PtacRoomExteriorEdge {
  const placed = alongWallClearOfOpenings(candidate, along);
  return {
    edge: candidate.edge,
    wall: candidate.wall,
    lengthPx: candidate.lengthPx,
    position: positionOnWall(candidate.wall, placed.along),
    rotationDeg: candidate.rotationDeg,
    fitsUnit: placed.fitsUnit
  };
}

function placementWithoutExteriorWall(
  room: GridSpaceDataType
): PtacUnitPlacement {
  const bounds = roomBoundsInGrid(room);
  const top = { start: bounds.left, end: bounds.right };
  return {
    position: {
      x: clampAlongWall(wallMidpoint(top), top),
      y: bounds.top - HALF_UNIT
    },
    edge: null,
    rotationDeg: 0
  };
}

export function ptacLevelContext(
  spaces: GridSpaceDataType[],
  footprints: FootprintPolygonsType | null | undefined,
  openings: PtacWallOpeningsLookup = noPtacWallOpenings
): PtacLevelContext {
  return { spaces, footprints: footprints ?? [], openings };
}

export function ptacRoomExteriorEdges(
  room: GridSpaceDataType,
  level: PtacLevelContext
): PtacRoomExteriorEdge[] {
  return exteriorWallCandidates(room, level).map((candidate) =>
    candidatePlacement(candidate, wallMidpoint(fullWallSpan(candidate.wall)))
  );
}

export function ptacRoomHasExteriorWall(
  room: GridSpaceDataType,
  level: PtacLevelContext
): boolean {
  return exteriorWallCandidates(room, level).length > 0;
}

export function defaultPtacUnitPlacement(
  room: GridSpaceDataType,
  level: PtacLevelContext
): PtacUnitPlacement {
  const candidates = exteriorWallCandidates(room, level);
  if (candidates.length === 0) {
    return placementWithoutExteriorWall(room);
  }
  const longest = candidatesThatFitUnit(candidates).reduce((best, candidate) =>
    candidate.lengthPx > best.lengthPx ? candidate : best
  );
  const placed = candidatePlacement(
    longest,
    wallMidpoint(fullWallSpan(longest.wall))
  );
  return {
    position: placed.position,
    edge: placed.edge,
    rotationDeg: placed.rotationDeg
  };
}

const squaredDistance = (a: PositionType, b: PositionType): number =>
  (a.x - b.x) ** 2 + (a.y - b.y) ** 2;

export function snapPtacUnitToRoom(
  room: GridSpaceDataType,
  level: PtacLevelContext,
  target: PositionType
): PositionType {
  const candidates = exteriorWallCandidates(room, level);
  if (candidates.length === 0) {
    return defaultPtacUnitPlacement(room, level).position;
  }
  return candidatesThatFitUnit(candidates)
    .map(
      (candidate) =>
        candidatePlacement(candidate, targetAlongWall(candidate.wall, target))
          .position
    )
    .reduce((best, candidate) =>
      squaredDistance(candidate, target) < squaredDistance(best, target)
        ? candidate
        : best
    );
}
